/**
 * #/teams — la base de equipos de la federación (CRUD sobre `/api/teams`).
 *
 * Es el equivalente a los archivos de club/temporada de DataVolley: el plantel
 * se carga una vez y la captura arma el `match_created` copiando el objeto.
 * Por eso el body es exactamente `TeamInfo` (ver `src/lib/team-form.ts`).
 */
import { useEffect, useState } from 'react';
import type { TFunction } from '../i18n/index';
import { useT } from '../i18n/use-t';
import { Card, Empty, ErrorBox, Loading } from '../components/States';
import { errorMessage, useAsync } from '../hooks';
import type { ApiClient, TeamRecord } from '../lib/api-client';
import { dateTime } from '../lib/format';
import {
  EMPTY_TEAM_FORM,
  buildTeamPayload,
  playersToText,
  roleLabels,
  slugifyTeamId,
  type TeamFormValues,
} from '../lib/team-form';

function toForm(team: TeamRecord, t: TFunction): TeamFormValues {
  return {
    teamId: team.id,
    name: team.name,
    code: team.code ?? '',
    coach: team.coach ?? '',
    playersText: playersToText(team.players, t),
    liberosText: team.liberos.join(', '),
  };
}

export function TeamsPage({ client }: { client: ApiClient }): JSX.Element {
  const teams = useAsync(() => client.listTeams(), [client]);
  const [editing, setEditing] = useState<TeamFormValues | null>(null);
  /** `null` = alta; con id = edición (el id queda bloqueado). */
  const [existingId, setExistingId] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const t = useT();

  // Al crear, el id se propone desde el nombre hasta que el usuario lo toque.
  const [idTouched, setIdTouched] = useState(false);
  useEffect(() => {
    if (existingId !== null || idTouched || editing === null) return;
    const proposed = slugifyTeamId(editing.name);
    if (proposed !== editing.teamId) setEditing({ ...editing, teamId: proposed });
  }, [editing, existingId, idTouched]);

  const startNew = (): void => {
    setEditing({ ...EMPTY_TEAM_FORM });
    setExistingId(null);
    setIdTouched(false);
    setErrors([]);
    setNotice(null);
  };

  const startEdit = (team: TeamRecord): void => {
    setEditing(toForm(team, t));
    setExistingId(team.id);
    setIdTouched(true);
    setErrors([]);
    setNotice(null);
  };

  const save = async (): Promise<void> => {
    if (!editing) return;
    const payload = buildTeamPayload(editing, t);
    if (!payload.ok) {
      setErrors(payload.errors);
      return;
    }
    setErrors([]);
    setSaving(true);
    try {
      await client.saveTeam(payload.teamId, payload.info);
      setNotice(t('teams.savedNotice', { name: payload.info.name }));
      setEditing(null);
      setExistingId(null);
      teams.reload();
    } catch (err) {
      setErrors([errorMessage(err instanceof Error ? err : new Error(String(err)))]);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (team: TeamRecord): Promise<void> => {
    // Confirmación explícita: borrar un plantel no se deshace.
    if (!window.confirm(t('teams.confirmDelete', { name: team.name }))) return;
    setNotice(null);
    try {
      await client.deleteTeam(team.id);
      setNotice(t('teams.deletedNotice', { name: team.name }));
      if (existingId === team.id) {
        setEditing(null);
        setExistingId(null);
      }
      teams.reload();
    } catch (err) {
      setErrors([errorMessage(err instanceof Error ? err : new Error(String(err)))]);
    }
  };

  return (
    <div>
      <div className="page-head">
        <h1>{t('teams.title')}</h1>
        <div className="row">
          <button type="button" onClick={teams.reload}>
            {t('common.refresh')}
          </button>
          <button type="button" className="primary" onClick={startNew}>
            {t('teams.newTeam')}
          </button>
        </div>
      </div>

      {notice ? <p className="hint">{notice}</p> : null}
      {teams.status === 'error' ? (
        <ErrorBox message={errorMessage(teams.error)} onRetry={teams.reload} />
      ) : null}

      {editing ? (
        <Card
          title={
            existingId === null ? t('teams.newTeam') : t('teams.editing', { id: existingId })
          }
        >
          {errors.length > 0 ? (
            <div className="error-box" role="alert">
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {errors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="grid-2">
            <label className="field">
              <span>{t('teams.name')}</span>
              <input
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                placeholder={t('teams.namePlaceholder')}
              />
            </label>
            <label className="field">
              <span>{t('teams.teamId')}</span>
              <input
                value={editing.teamId}
                disabled={existingId !== null}
                onChange={(e) => {
                  setIdTouched(true);
                  setEditing({ ...editing, teamId: e.target.value });
                }}
                placeholder={t('teams.teamIdPlaceholder')}
              />
            </label>
            <label className="field">
              <span>{t('teams.code')}</span>
              <input
                value={editing.code}
                onChange={(e) => setEditing({ ...editing, code: e.target.value })}
                placeholder={t('teams.codePlaceholder')}
              />
            </label>
            <label className="field">
              <span>{t('teams.coach')}</span>
              <input
                value={editing.coach}
                onChange={(e) => setEditing({ ...editing, coach: e.target.value })}
              />
            </label>
          </div>

          <label className="field">
            <span>{t('teams.roster', { roles: Object.values(roleLabels(t)).join(', ') })}</span>
            <textarea
              rows={10}
              value={editing.playersText}
              onChange={(e) => setEditing({ ...editing, playersText: e.target.value })}
              placeholder={t('teams.rosterPlaceholder')}
            />
          </label>

          <label className="field">
            <span>{t('teams.liberos')}</span>
            <input
              value={editing.liberosText}
              onChange={(e) => setEditing({ ...editing, liberosText: e.target.value })}
              placeholder={t('teams.liberosPlaceholder')}
            />
          </label>

          <div className="row">
            <button type="button" className="primary" disabled={saving} onClick={() => void save()}>
              {saving ? t('common.saving') : t('common.save')}
            </button>
            <button type="button" onClick={() => setEditing(null)} disabled={saving}>
              {t('common.cancel')}
            </button>
          </div>
        </Card>
      ) : null}

      <Card>
        {teams.status === 'loading' && teams.first ? <Loading what={t('teams.loadingWhat')} /> : null}
        {teams.status === 'ok' && (teams.data ?? []).length === 0 ? (
          <Empty>{t('teams.empty')}</Empty>
        ) : null}
        {(teams.data ?? []).length > 0 ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('teams.colTeam')}</th>
                  <th>{t('teams.colCode')}</th>
                  <th>{t('teams.colCoach')}</th>
                  <th className="num">{t('teams.colPlayers')}</th>
                  <th className="num">{t('teams.colLiberos')}</th>
                  <th>{t('teams.colUpdated')}</th>
                  <th>{t('teams.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {(teams.data ?? []).map((team) => (
                  <tr key={team.id}>
                    <td>
                      <strong>{team.name}</strong>
                      <div className="hint">{team.id}</div>
                    </td>
                    <td>{team.code ?? '·'}</td>
                    <td>{team.coach ?? '·'}</td>
                    <td className="num">{team.players.length}</td>
                    <td className="num">{team.liberos.length}</td>
                    <td>{dateTime(team.updatedAt)}</td>
                    <td>
                      <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                        <button type="button" className="small" onClick={() => startEdit(team)}>
                          {t('teams.edit')}
                        </button>
                        <button
                          type="button"
                          className="small danger"
                          onClick={() => void remove(team)}
                        >
                          {t('teams.remove')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
